/**
 * «منشی هوشمند» on a desk — the record button, at the foot of the sidebar (PM-0080).
 *
 * One height in every state, so nothing on the screen moves when it is pressed:
 *   ready      the microphone, «منشی هوشمند» and F1 — press to record;
 *   recording  RecordCapsule — stop, the time, the live level, ×;
 *   processing the plain row again with a spinner: the microphone is off, and a
 *              press records the next command while this one finishes;
 *   warn       why nothing was recorded («چیزی نشنیدم», the microphone refused),
 *              for a few seconds.
 * On the folded rail only the icon (or the stop button) stays.
 */
import React from 'react';
import { Mic } from 'lucide-react';

import { C } from '../constants/theme';
import { useTranslation } from '../i18n';
import { Kbd } from '../lib/shortcuts.jsx';
import RecordCapsule, { AMBER, SPIN_CSS } from './RecordCapsule';
import { useSecretaryRecorder } from './SecretaryHost';

/** Why the button is not recording, in the reader's words. */
export function warnText(warn, t) {
  if (!warn) return '';
  if (warn.kind === 'mic') return t(`secretary.recorder.warn.mic.${['denied', 'unsupported'].includes(warn.detail) ? warn.detail : 'error'}`);
  if (warn.kind === 'busy') return t('secretary.recorder.warn.busy');
  return t('secretary.recorder.warn.nothing');
}

export default function SecretaryRecorder({ collapsed = false, iconCell = 46 }) {
  const c = useSecretaryRecorder();
  const { t } = useTranslation();
  if (!c) return null;
  const box = { width: 'calc(100% - 20px)', margin: '6px 10px 8px', height: 44, boxSizing: 'border-box', flexShrink: 0 };
  if (c.recording) {
    return <div style={box}><RecordCapsule controls={c} collapsed={collapsed} /></div>;
  }
  const fade = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.22s ease',
    pointerEvents: collapsed ? 'none' : 'auto',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };
  const state = c.warn ? 'warn' : c.pending > 0 ? 'processing' : 'idle';
  const label = state === 'warn' ? warnText(c.warn, t)
    : state === 'processing' ? t('secretary.recorder.processing') : t('secretary.navLabel');
  const icon = state === 'warn' ? (
    <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(255, 179, 64, 0.18)', color: AMBER, fontWeight: 800, fontSize: 14 }}>!</span>
  ) : state === 'processing' ? (
    <span
      aria-hidden="true"
      className="secretary-spin"
      style={{ width: 16, height: 16, borderRadius: 999, boxSizing: 'border-box', border: '2px solid rgba(255, 255, 255, 0.3)', borderTopColor: C.tealLight }}
    />
  ) : <Mic size={19} aria-hidden="true" />;
  return (
    <button
      type="button"
      data-testid="nav-secretary"
      data-state={state}
      onClick={c.start}
      title={collapsed ? label : t('secretary.recorder.record')}
      aria-label={state === 'idle' ? t('secretary.recorder.record') : `${label} · ${t('secretary.recorder.record')}`}
      aria-keyshortcuts="F1"
      style={{
        ...box,
        display: 'flex', alignItems: 'center', padding: 0, borderRadius: 12, cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.22)', background: 'rgba(255, 255, 255, 0.08)',
        color: 'white', fontFamily: 'inherit',
      }}
    >
      <style>{SPIN_CSS}</style>
      <span style={{ width: iconCell, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: state === 'warn' ? AMBER : C.tealLight }}>
        {icon}
      </span>
      <span
        aria-live="polite"
        style={{ ...fade, flex: 1, textAlign: 'start', fontSize: 14, fontWeight: 700, color: state === 'warn' ? AMBER : 'white' }}
      >
        {label}
      </span>
      <span style={{ ...fade, marginInlineEnd: 12, display: 'inline-flex' }}>
        <Kbd combo="f1" />
      </span>
    </button>
  );
}
